/* Typed fetch client wrapping /api/v1.
 * Reads the CSRF cookie on every unsafe call and echoes it back as X-CSRFToken.
 */

export type ApiErrorPayload = { code: string; message: string; details?: unknown };
export class ApiError extends Error {
  code: string;
  details?: unknown;
  status: number;
  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.code = payload.code;
    this.details = payload.details;
    this.status = status;
  }
}

const BASE = "/api/v1";
export const AUTH_REQUIRED_EVENT = "oe:auth-required";
/** Dispatched on a 402 UPGRADE_REQUIRED so the shell can open an upgrade prompt.
 * detail: { feature?: string; message: string } */
export const UPGRADE_REQUIRED_EVENT = "oe:upgrade-required";

let lastAuthRequiredEventAt = 0;
let lastUpgradeEventAt = 0;

function getCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = getCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers,
  });
  if (!res.ok) {
    const body = await safeJson(res);
    const err: ApiErrorPayload = body?.error ?? {
      code: "UNKNOWN",
      message: res.statusText || "Request failed",
    };
    if (shouldRedirectToLogin(path, res.status, err)) {
      emitAuthRequired(path);
    }
    if (res.status === 402 && err.code === "UPGRADE_REQUIRED") {
      emitUpgradeRequired(err);
    }
    throw new ApiError(res.status, err);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

function shouldRedirectToLogin(path: string, status: number, err: ApiErrorPayload): boolean {
  if (status !== 401 || err.code !== "AUTH_REQUIRED") return false;
  if (path === "/me" || path === "/auth/login") return false;
  return true;
}

function emitAuthRequired(path: string): void {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastAuthRequiredEventAt < 500) return;
  lastAuthRequiredEventAt = now;
  window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT, { detail: { path } }));
}

function emitUpgradeRequired(err: ApiErrorPayload): void {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastUpgradeEventAt < 500) return;
  lastUpgradeEventAt = now;
  const feature = (err.details as { feature?: string } | undefined)?.feature;
  window.dispatchEvent(
    new CustomEvent(UPGRADE_REQUIRED_EVENT, { detail: { feature, message: err.message } }),
  );
}

/** Open the upgrade prompt proactively (e.g. clicking a locked nav item),
 * without waiting for the backend to return a 402. */
export function requestUpgrade(feature?: string, message?: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(UPGRADE_REQUIRED_EVENT, { detail: { feature, message } }));
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
